/**
 * ModeProfileCard.js
 *
 * Profile card that opens the "More" / menu screens of the Sales and Service
 * app modes: avatar on an orange ring, the user's name, a tinted role chip and
 * the e-mail address. Presentation only — callers pass the already-resolved
 * user and decide what a tap does.
 *
 * @module components/ModeProfileCard
 */

import React from 'react';
import {Pressable, StyleSheet, Text, View} from 'react-native';
import Avatar from './Avatar';
import Icon from './management/SafeIcon';
import {useManagementTheme} from '../helpers/managementTheme';

/**
 * Premium profile card (white card, hairline border, soft shadow).
 *
 * @param {object} props
 * @param {?object} [props.user] Stored user record, used for the avatar photo / initials.
 * @param {string} props.name Display name (falls back to the role label when empty).
 * @param {string} [props.roleLabel] Text of the tinted chip (e.g. the app mode name).
 * @param {string} [props.email] Secondary line under the chip.
 * @param {Function} [props.onPress] Opens the profile / settings; the card is inert without it.
 * @returns {JSX.Element} The profile card.
 */
export default function ModeProfileCard({user, name, roleLabel, email, onPress}) {
    const m = useManagementTheme();
    const title = name || roleLabel || '';

    return (
        <Pressable
            onPress={onPress}
            disabled={!onPress}
            accessibilityRole={onPress ? 'button' : undefined}
            style={({pressed}) => [
                styles.card,
                {backgroundColor: m.colors.surface, borderColor: m.colors.border, opacity: pressed ? 0.92 : 1},
                m.shadow(1),
            ]}
        >
            <View style={[styles.ring, {borderColor: m.colors.primary}]}>
                <Avatar user={user || title} size={58} />
            </View>
            <View style={styles.text}>
                <Text style={[styles.name, {color: m.colors.heading}]} numberOfLines={1}>{title}</Text>
                {!!roleLabel && (
                    <View style={[styles.roleChip, {backgroundColor: m.colors.chipBg}]}>
                        <Text style={[styles.roleText, {color: m.colors.chipText}]} numberOfLines={1}>{roleLabel}</Text>
                    </View>
                )}
                {!!email && <Text style={[styles.email, {color: m.colors.subText}]} numberOfLines={1}>{email}</Text>}
            </View>
            {!!onPress && <Icon name="chevron-right" size={22} color={m.colors.muted} />}
        </Pressable>
    );
}

const styles = StyleSheet.create({
    card: {flexDirection: 'row', alignItems: 'center', gap: 14, borderRadius: 26, borderWidth: 1, padding: 16, marginBottom: 6},
    ring: {borderWidth: 2, borderRadius: 34, padding: 3},
    text: {flex: 1, minWidth: 0},
    name: {fontSize: 20, fontWeight: '800', letterSpacing: -0.4},
    roleChip: {alignSelf: 'flex-start', borderRadius: 999, paddingHorizontal: 10, paddingVertical: 3, marginTop: 6},
    roleText: {fontSize: 12, fontWeight: '800'},
    email: {fontSize: 12.5, fontWeight: '500', marginTop: 6},
});
